/**
 * Utility functions for timezone conversions in the chart
 */

/**
 * Adjusts a UTC timestamp to the selected timezone, accounting for DST
 * @param {number} timestamp - UTC timestamp in seconds
 * @param {Object} timezone - The timezone object with timezone code
 * @returns {number} - Adjusted timestamp in seconds
 */
export const adjustTimestampToTimezone = (timestamp, timezone) => {
  if (!timezone || timezone.code === 'UTC' || timezone.code === 'Exchange') {
    return timestamp; // No adjustment needed for UTC or Exchange
  }
  
  try {
    // Convert to milliseconds for Date operations
    const timestampMs = timestamp * 1000;
    const utcDate = new Date(timestampMs);
    
    // Format in target timezone to account for DST
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone.code,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false
    });
    
    // Parse the formatted date in target timezone
    const parts = formatter.formatToParts(utcDate);
    const tzParts = {};
    
    parts.forEach(part => {
      if (part.type !== 'literal') {
        tzParts[part.type] = parseInt(part.value, 10);
      }
    });
    
    // Create a date object in UTC but with the target timezone's time
    const targetDate = Date.UTC(
      tzParts.year,
      tzParts.month - 1,
      tzParts.day,
      tzParts.hour,
      tzParts.minute,
      tzParts.second,
      utcDate.getUTCMilliseconds()
    ) / 1000; // Convert back to seconds
    
    return targetDate;
  } catch (error) {
    console.error("Error adjusting timestamp to timezone:", error);
    
    // Fallback to simple offset adjustment if the Intl API fails
    const offsetMs = timezone.offset * 60 * 60 * 1000;
    return (timestamp * 1000 + offsetMs) / 1000;
  }
};

/**
 * Converts a batch of chart data points to the selected timezone
 * @param {Array} data - Array of chart data points with time property
 * @param {Object} timezone - The timezone object with timezone code
 * @returns {Array} - Adjusted chart data
 */
export const convertDataToTimezone = (data, timezone) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return data;
  }
  
  if (!timezone || timezone.code === 'UTC' || timezone.code === 'Exchange') {
    return data; // No conversion needed for UTC or Exchange
  }
  
  return data.map(point => {
    // Create a new object to avoid mutating the original
    return {
      ...point,
      time: adjustTimestampToTimezone(point.time, timezone)
    };
  });
};

/**
 * Formats a timestamp according to the selected timezone
 * @param {number} timestamp - UTC timestamp in seconds
 * @param {Object} timezone - The timezone object
 * @param {string} format - Display format (optional)
 * @returns {string} - Formatted time string
 */
export const formatTimeWithTimezone = (timestamp, timezone, format = 'hh:mm:ss') => {
  if (!timestamp) return '';
  
  // Convert to milliseconds
  const timestampMs = timestamp * 1000;
  
  // Format options
  const options = {
    hour: '2-digit',
    minute: '2-digit',
    second: format.includes('ss') ? '2-digit' : undefined,
    timeZone: timezone?.code || 'UTC',
    hour12: format.includes('hh')
  };
  
  try {
    return new Intl.DateTimeFormat('en-US', options).format(new Date(timestampMs));
  } catch (error) {
    console.error("Error formatting time with timezone:", error);
    
    // Fallback to basic formatting
    const date = new Date(timestampMs);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}${format.includes('ss') ? `:${seconds.toString().padStart(2, '0')}` : ''}`;
  }
};

/**
 * Determines if a timezone is currently in DST
 * @param {Object} timezone - The timezone object
 * @returns {boolean} - True if timezone is in DST
 */
export const isTimezoneDST = (timezone) => {
  if (!timezone || timezone.code === 'UTC' || timezone.code === 'Exchange') {
    return false; // UTC and Exchange are never in DST
  }
  
  try {
    // Compare January (standard time) with July (likely DST in northern hemisphere)
    const january = new Date(new Date().getFullYear(), 0, 1);
    const july = new Date(new Date().getFullYear(), 6, 1);
    
    const januaryFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone.code,
      timeZoneName: 'short'
    });
    
    const julyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone.code,
      timeZoneName: 'short'
    });
    
    // Extract timezone abbreviations
    const januaryTZ = januaryFormatter.formatToParts(january)
      .find(part => part.type === 'timeZoneName')?.value;
    
    const julyTZ = julyFormatter.formatToParts(july)
      .find(part => part.type === 'timeZoneName')?.value;
    
    // If abbreviations differ, the timezone observes DST
    return januaryTZ !== julyTZ;
  } catch (error) {
    console.error("Error checking DST status:", error);
    return false;
  }
};

/**
 * Get a list of common timezones
 * @returns {Array} - List of timezone objects
 */
export const getCommonTimezones = () => {
  // Base timezone list with manually specified offsets
  const timezones = [
    { name: 'UTC', offset: 0, code: 'UTC' },
    { name: 'Exchange', offset: 0, code: 'Exchange' },
    { name: 'Honolulu', offset: -10, code: 'Pacific/Honolulu' },
    { name: 'Anchorage', offset: -8, code: 'America/Anchorage' },
    { name: 'Juneau', offset: -8, code: 'America/Juneau' },
    { name: 'Los Angeles', offset: -7, code: 'America/Los_Angeles' },
    { name: 'Phoenix', offset: -7, code: 'America/Phoenix' },
    { name: 'Vancouver', offset: -7, code: 'America/Vancouver' },
    { name: 'Denver', offset: -6, code: 'America/Denver' },
    { name: 'Mexico City', offset: -6, code: 'America/Mexico_City' },
    { name: 'San Salvador', offset: -6, code: 'America/El_Salvador' },
    { name: 'Bogota', offset: -5, code: 'America/Bogota' },
    { name: 'Chicago', offset: -5, code: 'America/Chicago' },
    { name: 'Lima', offset: -5, code: 'America/Lima' },
    { name: 'Caracas', offset: -4, code: 'America/Caracas' },
    { name: 'New York', offset: -4, code: 'America/New_York' },
    { name: 'Santiago', offset: -4, code: 'America/Santiago' },
    { name: 'Toronto', offset: -4, code: 'America/Toronto' },
    { name: 'Buenos Aires', offset: -3, code: 'America/Buenos_Aires' },
    { name: 'Sao Paulo', offset: -3, code: 'America/Sao_Paulo' },
    { name: 'Azores', offset: 0, code: 'Atlantic/Azores' },
    { name: 'Reykjavik', offset: 0, code: 'Atlantic/Reykjavik' },
    { name: 'Casablanca', offset: 1, code: 'Africa/Casablanca' },
    { name: 'Dublin', offset: 1, code: 'Europe/Dublin' },
    { name: 'Lagos', offset: 1, code: 'Africa/Lagos' },
    { name: 'Lisbon', offset: 1, code: 'Europe/Lisbon' },
    { name: 'London', offset: 1, code: 'Europe/London' },
    { name: 'Tunis', offset: 1, code: 'Africa/Tunis' },
    { name: 'Amsterdam', offset: 2, code: 'Europe/Amsterdam' },
    { name: 'Berlin', offset: 2, code: 'Europe/Berlin' },
    { name: 'Brussels', offset: 2, code: 'Europe/Brussels' },
    { name: 'Paris', offset: 2, code: 'Europe/Paris' },
    { name: 'Rome', offset: 2, code: 'Europe/Rome' },
    { name: 'Zurich', offset: 2, code: 'Europe/Zurich' },
    { name: 'Athens', offset: 3, code: 'Europe/Athens' },
    { name: 'Istanbul', offset: 3, code: 'Europe/Istanbul' },
    { name: 'Moscow', offset: 3, code: 'Europe/Moscow' },
    { name: 'Dubai', offset: 4, code: 'Asia/Dubai' },
    { name: 'Mumbai', offset: 5.5, code: 'Asia/Kolkata' },
    { name: 'Bangkok', offset: 7, code: 'Asia/Bangkok' },
    { name: 'Jakarta', offset: 7, code: 'Asia/Jakarta' },
    { name: 'Hong Kong', offset: 8, code: 'Asia/Hong_Kong' },
    { name: 'Shanghai', offset: 8, code: 'Asia/Shanghai' },
    { name: 'Singapore', offset: 8, code: 'Asia/Singapore' },
    { name: 'Taipei', offset: 8, code: 'Asia/Taipei' },
    { name: 'Seoul', offset: 9, code: 'Asia/Seoul' },
    { name: 'Tokyo', offset: 9, code: 'Asia/Tokyo' },
    { name: 'Sydney', offset: 10, code: 'Australia/Sydney' },
    { name: 'Auckland', offset: 12, code: 'Pacific/Auckland' },
    { name: 'Local', offset: new Date().getTimezoneOffset() / -60, code: Intl.DateTimeFormat().resolvedOptions().timeZone }
  ];
  
  // Update offsets based on current DST status
  return timezones.map(tz => {
    if (tz.code === 'UTC' || tz.code === 'Exchange') {
      return tz; // No DST adjustment needed
    }
    
    try {
      // Get the current offset for this timezone
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz.code,
        timeZoneName: 'longOffset'
      });
      
      const tzString = formatter.formatToParts(now)
        .find(part => part.type === 'timeZoneName')?.value || '';
      
      // Extract current offset from string like "GMT+01:00"
      const match = tzString.match(/GMT([+-])(\d{2}):(\d{2})/);
      
      if (match) {
        const sign = match[1] === '-' ? -1 : 1;
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3], 10);
        const currentOffset = sign * (hours + minutes / 60);
        
        // Check if DST is active by comparing with the base offset
        const isDST = isTimezoneDST(tz) && currentOffset !== tz.offset;
        
        // Return updated timezone object with current offset and DST flag
        return {
          ...tz,
          currentOffset: currentOffset,
          isDST: isDST
        };
      }
    } catch (error) {
      console.error(`Error calculating current offset for ${tz.name}:`, error);
    }
    
    // Fallback to original offset if calculation fails
    return tz;
  });
};

/**
 * Gets the browser's local timezone
 * @returns {Object} - Timezone object
 */
export const getLocalTimezone = () => {
  const localTzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  try {
    // Get current offset
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: localTzName,
      timeZoneName: 'longOffset'
    });
    
    const tzString = formatter.formatToParts(now)
      .find(part => part.type === 'timeZoneName')?.value || '';
    
    // Extract current offset from string like "GMT+01:00"
    const match = tzString.match(/GMT([+-])(\d{2}):(\d{2})/);
    
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);
      const currentOffset = sign * (hours + minutes / 60);
      
      // Get standard time offset
      const january = new Date(new Date().getFullYear(), 0, 1);
      const januaryFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: localTzName,
        timeZoneName: 'longOffset'
      });
      
      const januaryTzString = januaryFormatter.formatToParts(january)
        .find(part => part.type === 'timeZoneName')?.value || '';
      
      const januaryMatch = januaryTzString.match(/GMT([+-])(\d{2}):(\d{2})/);
      
      if (januaryMatch) {
        const januarySign = januaryMatch[1] === '-' ? -1 : 1;
        const januaryHours = parseInt(januaryMatch[2], 10);
        const januaryMinutes = parseInt(januaryMatch[3], 10);
        const baseOffset = januarySign * (januaryHours + januaryMinutes / 60);
        
        // Check if DST is active
        const isDST = currentOffset !== baseOffset;
        
        return {
          name: 'Local',
          offset: baseOffset,
          currentOffset: currentOffset,
          isDST: isDST,
          code: localTzName
        };
      }
    }
  } catch (error) {
    console.error("Error calculating local timezone info:", error);
  }
  
  // Fallback to simple offset calculation
  const localTzOffset = new Date().getTimezoneOffset() / -60;
  
  return {
    name: 'Local',
    offset: localTzOffset,
    currentOffset: localTzOffset,
    code: localTzName
  };
};

/**
 * Format timezone offset for display with correct sign
 * @param {number} offset - The timezone offset in hours
 * @returns {string} - Formatted offset string
 */
export const formatTimezoneOffset = (offset) => {
  // Always display the sign (+ or -)
  const sign = offset >= 0 ? '+' : '-';
  
  // Handle the hours and minutes parts
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset);
  const minutes = Math.round((absOffset - hours) * 60);
  
  // Format with appropriate padding and display
  return `${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}`;
}; 